import { useSyncExternalStore } from 'react';

/**
 * Server-rail placement: a horizontal bar on top, or a vertical rail on the
 * left. Per-device choice → localStorage, reactive via useSyncExternalStore.
 *
 * Default is top (a horizontal strip) until the user picks one explicitly,
 * after which the choice wins in both skins.
 */
export type RailPlacement = 'top' | 'left';

const STORAGE_KEY = 'bunker.railPlacement';
const listeners = new Set<() => void>();

/** The explicit stored choice, or null when the user hasn't picked one. */
function storedPlacement(): RailPlacement | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'top' || v === 'left' ? v : null;
  } catch {
    return null;
  }
}

/** Effective placement — explicit choice, else top. */
export function getRailPlacement(): RailPlacement {
  return storedPlacement() ?? 'top';
}

export function setRailPlacement(p: RailPlacement): void {
  if (p === getRailPlacement() && storedPlacement() === p) return;
  try {
    localStorage.setItem(STORAGE_KEY, p);
  } catch {
    /* private mode / quota — session-only state still works */
  }
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useRailPlacement(): RailPlacement {
  return useSyncExternalStore(subscribe, getRailPlacement, getRailPlacement);
}
